import { Link } from "react-router-dom";
import { FileText, Trash2, Download, ArrowRight, FileType } from "lucide-react";
import { format } from "date-fns";
import { deleteResume, pdfUrl, docxUrl } from "@/lib/api";
import { toast } from "sonner";

function getInitials(name) {
  if (!name) return "??";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Stable hue from string — looks like a friendly avatar without revealing PII
function hashHue(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % 360;
}

export default function ResumeList({ resumes, onChanged }) {
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this parsed resume?")) return;
    try {
      await deleteResume(id);
      toast.success("Deleted");
      onChanged?.();
    } catch {
      toast.error("Delete failed");
    }
  };

  if (!resumes.length) {
    return (
      <div className="swiss-card p-14 text-center" data-testid="resume-list-empty">
        <div className="relative inline-flex items-center justify-center w-20 h-20 mb-5">
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[var(--brand-primary)]/15 to-[var(--brand-accent)]/15 animate-float" />
          <div className="relative w-14 h-14 rounded-full bg-gradient-to-br from-[var(--brand-primary)] to-[var(--brand-accent)] flex items-center justify-center shadow-md animate-bounce-in">
            <FileText className="w-7 h-7 text-white" strokeWidth={1.5} />
          </div>
          <svg className="absolute -top-1 -right-1 w-4 h-4 text-amber-400 animate-sparkle" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 0l3 9 9 3-9 3-3 9-3-9-9-3 9-3z" />
          </svg>
          <svg className="absolute bottom-0 -left-2 w-3 h-3 text-pink-400 animate-sparkle" viewBox="0 0 24 24" fill="currentColor" style={{ animationDelay: "-1s" }}>
            <path d="M12 0l3 9 9 3-9 3-3 9-3-9-9-3 9-3z" />
          </svg>
        </div>
        <div className="font-heading font-semibold text-lg text-[var(--text-primary)] mb-1.5">
          A blank canvas awaits.
        </div>
        <p className="text-sm text-[var(--text-secondary)]">
          Upload your first resume above and watch the magic happen ✨
        </p>
      </div>
    );
  }

  return (
    <div className="swiss-card overflow-hidden" data-testid="resume-list">
      <div className="grid grid-cols-12 px-6 py-3.5 border-b border-[var(--border-default)] bg-[var(--bg-tint)]/50 text-[11px] uppercase tracking-wider font-semibold text-[var(--text-muted)]">
        <div className="col-span-1">#</div>
        <div className="col-span-4">Candidate</div>
        <div className="col-span-3 hidden md:block">Title</div>
        <div className="col-span-2 hidden md:block">Parsed</div>
        <div className="col-span-2 text-right">Actions</div>
      </div>
      {resumes.map((r, idx) => {
        const initials = getInitials(r.full_name);
        const hue = hashHue(r.id || r.full_name || String(idx));
        return (
          <div
            key={r.id}
            data-testid={`resume-row-${r.id}`}
            className="grid grid-cols-12 px-6 py-4 border-b last:border-b-0 border-[var(--border-default)] hover:bg-[var(--bg-tint)]/40 transition-colors items-center"
          >
            <div className="col-span-1 text-xs text-[var(--text-muted)] font-mono">
              {String(idx + 1).padStart(2, "0")}
            </div>
            <Link
              to={`/resume/${r.id}`}
              className="col-span-11 md:col-span-4 flex items-center gap-3 group"
              data-testid={`resume-name-link-${r.id}`}
            >
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0 shadow-sm"
                style={{
                  backgroundColor: `hsl(${hue}, 50%, 45%)`,
                  backgroundImage: `linear-gradient(135deg, hsl(${hue}, 55%, 50%) 0%, hsl(${(hue + 30) % 360}, 60%, 40%) 100%)`,
                }}
                aria-hidden="true"
              >
                {initials}
              </div>
              <div className="min-w-0">
                <div className="font-heading font-semibold text-[15px] text-[var(--text-primary)] group-hover:text-[var(--brand-primary)] transition-colors truncate">
                  {r.full_name || "Unnamed"}
                </div>
                <div className="text-xs text-[var(--text-muted)] md:hidden mt-0.5 truncate">
                  {r.professional_title || "—"}
                </div>
              </div>
            </Link>
            <div className="col-span-3 hidden md:block text-sm text-[var(--text-secondary)] truncate">
              {r.professional_title || "—"}
            </div>
            <div className="col-span-2 hidden md:block text-xs text-[var(--text-muted)] font-medium">
              {r.created_at ? format(new Date(r.created_at), "dd MMM yyyy") : "—"}
            </div>
            <div className="col-span-12 md:col-span-2 flex items-center gap-1 justify-end mt-3 md:mt-0">
              <a
                href={pdfUrl(r.id)}
                target="_blank"
                rel="noreferrer"
                className="btn-ghost"
                data-testid={`download-pdf-${r.id}`}
                title="Download PDF"
              >
                <Download className="w-4 h-4" strokeWidth={1.75} />
              </a>
              <a
                href={docxUrl(r.id)}
                target="_blank"
                rel="noreferrer"
                className="btn-ghost"
                data-testid={`download-docx-${r.id}`}
                title="Download DOCX"
              >
                <FileType className="w-4 h-4" strokeWidth={1.75} />
              </a>
              <button
                onClick={() => handleDelete(r.id)}
                className="btn-ghost hover:!text-[var(--brand-danger)]"
                data-testid={`delete-resume-${r.id}`}
                title="Delete"
              >
                <Trash2 className="w-4 h-4" strokeWidth={1.75} />
              </button>
              <Link
                to={`/resume/${r.id}`}
                className="btn-ghost"
                data-testid={`view-resume-${r.id}`}
                title="View"
              >
                <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
              </Link>
            </div>
          </div>
        );
      })}
    </div>
  );
}
